// Simplified heartbeat hook: keep a websocket per session and rely on its connection state.
// Backend handles status changes whenever the socket connects or disconnects.
// Enhanced with Page Visibility API support for hidden windows.
import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { getApiBaseUrl } from '../utils/apiUrl';

const HEARTBEAT_WS_DISABLED = false; // Temporalmente deshabilita la conexión WebSocket del servidor

export const useHeartbeat = (api, agentId, options = {}) => {
  const {
    enabled = true,
    customEvents = {}
  } = options;

  const socketRef = useRef(null);
  const sessionIdRef = useRef(null);
  const isPageHiddenRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const watchdogRef = useRef(null);
  const { token } = useAuth();
  const storageKey = agentId ? `cti_heartbeat_session_${agentId}` : null;

  const readStoredSessionId = useCallback(() => {
    if (!storageKey || typeof window === 'undefined') return null;
    try {
      return window.sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }
  }, [storageKey]);

  const writeStoredSessionId = useCallback((value) => {
    if (!storageKey || typeof window === 'undefined' || !value) return;
    try {
      window.sessionStorage.setItem(storageKey, value);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const removeStoredSessionId = useCallback(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const ensureSessionId = useCallback(() => {
    if (!agentId) {
      return null;
    }
    if (sessionIdRef.current) {
      return sessionIdRef.current;
    }
    const persisted = readStoredSessionId();
    if (persisted) {
      sessionIdRef.current = persisted;
      return persisted;
    }
    let randomPart = null;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      randomPart = crypto.randomUUID();
    } else {
      randomPart = Math.random().toString(36).substring(2, 12);
    }
    const newId = `${agentId}-${randomPart}`;
    sessionIdRef.current = newId;
    writeStoredSessionId(newId);
    return newId;
  }, [agentId, readStoredSessionId, writeStoredSessionId]);

  // 🔒 NUEVO: Generar sessionId forzadamente nuevo (para recuperar sesión)
  const forceNewSessionId = useCallback(() => {
    if (!agentId) {
      return null;
    }
    let randomPart = null;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      randomPart = crypto.randomUUID();
    } else {
      randomPart = Math.random().toString(36).substring(2, 12);
    }
    const newId = `${agentId}-${randomPart}`;
    sessionIdRef.current = newId;
    writeStoredSessionId(newId);
    logger.log(`[HEARTBEAT]  Generated new sessionId for ${agentId}: ${newId}`);
    return newId;
  }, [agentId, writeStoredSessionId]);

  const getCurrentVisibility = useCallback(() => {
    if (typeof document === 'undefined') {
      return 'visible';
    }
    return document.hidden ? 'hidden' : 'visible';
  }, []);

  const getSocketServerURL = useCallback(() => {
    const base = api?.baseURL || getApiBaseUrl();
    try {
      if (base.startsWith('/')) {
        return window.location.origin;
      }
      const parsed = new URL(base);
      const portSegment = parsed.port ? `:${parsed.port}` : '';
      return `${parsed.protocol}//${parsed.hostname}${portSegment}`;
    } catch (error) {
      logger.warn('[HEARTBEAT] Could not parse API base URL, using current origin', error);
      return window.location.origin;
    }
  }, [api?.baseURL]);

  // Page Visibility API handlers
  const handleVisibilityChange = useCallback(() => {
    if (typeof document === 'undefined') return;

    const isHidden = document.hidden;
    isPageHiddenRef.current = isHidden;

    logger.log(`[HEARTBEAT] Page visibility changed: ${isHidden ? 'hidden' : 'visible'}`);

    if (!socketRef.current?.connected) return;

    socketRef.current.emit('heartbeat:visibility', {
      agentId,
      sessionId: sessionIdRef.current,
      visibility: isHidden ? 'hidden' : 'visible'
    });
  }, [agentId]);

  const disconnectSocket = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      if (typeof window !== 'undefined' && window.__heartbeatSocket === socketRef.current) {
        window.__heartbeatSocket = null;
      }
      socketRef.current = null;
    }
  }, []);

  const connectSocket = useCallback(() => {
    if (HEARTBEAT_WS_DISABLED) {
      return;
    }
    if (!enabled || !agentId || !token) {
      return;
    }
    if (socketRef.current?.connected) {
      return;
    }
    if (socketRef.current && !socketRef.current.connected) {
      socketRef.current.connect();
      return;
    }

    const sessionId = ensureSessionId();
    const socket = io(`${getSocketServerURL()}/heartbeat`, {
      // Keep HTTP polling as a fallback for networks that silently drop
      // long-lived WebSockets while HTTP/SSE remain operational.
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: false,
      auth: {
        token,
        agentId,
        sessionId,
        visibility: getCurrentVisibility()
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 15000
    });

    socketRef.current = socket;
    if (typeof window !== 'undefined') {
      window.__heartbeatSocket = socket;
    }

    socket.on('connect', () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      logger.log(`[HEARTBEAT] WebSocket connected for agent ${agentId}`);

      const currentVisibility = getCurrentVisibility();
      isPageHiddenRef.current = currentVisibility === 'hidden';

      socket.emit('heartbeat:visibility', {
        agentId,
        sessionId: sessionIdRef.current,
        visibility: currentVisibility
      });
    });

    socket.on('heartbeat:ack', (payload = {}) => {
      const ackId = payload.sessionId || payload.session_id;
      if (ackId) {
        sessionIdRef.current = ackId;
        writeStoredSessionId(ackId);
      }
    });

    // 🔒 NUEVO: Manejar sesión inválida del backend
    socket.on('session:invalid', (payload = {}) => {
      console.error(`[HEARTBEAT] 🛡️ Session invalid:`, payload);

      // Ejecutar callback para conflicto de sesión si está disponible
      if (customEvents['session:invalid']) {
        customEvents['session:invalid'](payload);
      } else {
        // Comportamiento por defecto: limpiar sesión local y desconectar
        sessionIdRef.current = null;
        removeStoredSessionId();
        disconnectSocket();

        // Mostrar alerta al usuario
        if (typeof window !== 'undefined') {
          alert('Tu sesión ya no es válida. Se detectó otra sesión activa. La página se recargará.');
          window.location.reload();
        }
      }
    });

    // Manejar timeout de heartbeat desde el backend
    socket.on('heartbeat:timeout', (payload = {}) => {
      logger.warn(`[HEARTBEAT] ⏰ Heartbeat timeout from backend:`, payload);

      // Ejecutar callback para timeout si está disponible
      if (customEvents['heartbeat:timeout']) {
        customEvents['heartbeat:timeout'](payload);
      } else {
        // Comportamiento por defecto: desconectar y mostrar alerta
        disconnectSocket();

        if (typeof window !== 'undefined') {
          alert('Tu sesión se cerró por inactividad. La página se recargará.');
          window.location.reload();
        }
      }
    });

    // Manejar eventos de desconexión del agente
    socket.on('agent:disconnected', (payload = {}) => {
      logger.warn(`[HEARTBEAT] 👤 Agent disconnected:`, payload);

      if (payload.reason === 'heartbeat_timeout') {
        // Ejecutar callback específico para timeout
        if (customEvents['agent:timeout']) {
          customEvents['agent:timeout'](payload);
        } else if (customEvents['heartbeat:timeout']) {
          customEvents['heartbeat:timeout'](payload);
        } else {
          // Comportamiento por defecto
          disconnectSocket();

          if (typeof window !== 'undefined') {
            alert('Tu sesión se cerró por inactividad. La página se recargará.');
            window.location.reload();
          }
        }
      } else {
        // Otros tipos de desconexión
        if (customEvents['agent:disconnected']) {
          customEvents['agent:disconnected'](payload);
        }
      }
    });

    socket.on('disconnect', (reason) => {
      logger.warn(`[HEARTBEAT] WebSocket disconnected for agent ${agentId}: ${reason}`);
      scheduleReconnect(reason);
    });

    socket.on('connect_error', (error) => {
      logger.warn(`[HEARTBEAT] WebSocket connect_error for agent ${agentId}: ${error?.message || error}`);
      scheduleReconnect('connect_error');
    });

    Object.entries(customEvents || {}).forEach(([eventName, handler]) => {
      if (typeof handler === 'function') {
        socket.on(eventName, handler);
      }
    });
  }, [enabled, agentId, token, customEvents, getSocketServerURL, ensureSessionId, writeStoredSessionId, getCurrentVisibility]);

  const scheduleReconnect = useCallback((reason) => {
    if (!enabled || !agentId || !token || HEARTBEAT_WS_DISABLED) {
      return;
    }
    if (reconnectTimerRef.current) {
      return;
    }
    logger.warn(`[HEARTBEAT] Scheduling reconnect for agent ${agentId} (reason: ${reason || 'unknown'})`);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      const socket = socketRef.current;
      if (socket?.connected) {
        return;
      }

      // Electron can retain a half-open Engine.IO manager after ping timeout.
      // Destroy it so connectSocket creates a genuinely new transport.
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        if (typeof window !== 'undefined' && window.__heartbeatSocket === socket) {
          window.__heartbeatSocket = null;
        }
        socketRef.current = null;
      }
      connectSocket();
    }, 2000);
  }, [enabled, agentId, token, connectSocket]);

  useEffect(() => {
    if (!enabled || HEARTBEAT_WS_DISABLED) {
      return undefined;
    }

    connectSocket();

    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
    watchdogRef.current = setInterval(() => {
      if (!enabled || HEARTBEAT_WS_DISABLED) return;
      const socket = socketRef.current;
      if (!socket) {
        connectSocket();
        return;
      }
      if (!socket.connected && !reconnectTimerRef.current) {
        socket.removeAllListeners();
        socket.disconnect();
        if (typeof window !== 'undefined' && window.__heartbeatSocket === socket) {
          window.__heartbeatSocket = null;
        }
        socketRef.current = null;
        connectSocket();
      }
    }, 15000);

    const handleBeforeUnload = () => {
      disconnectSocket();
    };

    // Set up Page Visibility API listeners
    const canUseDocument = typeof document !== 'undefined';
    const canUseWindow = typeof window !== 'undefined';

    if (canUseDocument) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    const handleOnline = () => {
      logger.log('[HEARTBEAT] Browser online — recreating transport');
      const socket = socketRef.current;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        if (window.__heartbeatSocket === socket) {
          window.__heartbeatSocket = null;
        }
        socketRef.current = null;
      }
      connectSocket();
    };

    if (canUseWindow) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('online', handleOnline);
    }

    return () => {
      if (canUseDocument) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      if (canUseWindow) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('online', handleOnline);
      }
      if (watchdogRef.current) {
        clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
      disconnectSocket();
    };
  }, [enabled, connectSocket, disconnectSocket, handleVisibilityChange]);

  const startHeartbeat = useCallback(() => {
    connectSocket();
  }, [connectSocket]);

  const stopHeartbeat = useCallback(() => {
    disconnectSocket();
  }, [disconnectSocket]);

  const emitRealtimeEvent = useCallback((eventName, payload = {}) => {
    if (!eventName) return false;
    if (socketRef.current?.connected) {
      socketRef.current.emit(eventName, payload);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(async () => {
    logger.log(`[HEARTBEAT] Starting logout for agent ${agentId}...`);

    // Primero notificar al backend
    if (api?.heartbeat && agentId) {
      try {
        logger.log(`[HEARTBEAT] Notifying backend logout for agent ${agentId}, sessionId: ${sessionIdRef.current}`);
        await api.heartbeat.logout(agentId, sessionIdRef.current);
        logger.log(`[HEARTBEAT] Backend logout completed for agent ${agentId}`);
      } catch (error) {
        console.error('[HEARTBEAT] Error notifying backend logout:', error);
      }
    }

    // Luego desconectar websocket y limpiar local
    disconnectSocket();
    sessionIdRef.current = null;
    removeStoredSessionId();

    logger.log(`[HEARTBEAT] Local logout completed for agent ${agentId}`);
  }, [disconnectSocket, api?.heartbeat, agentId, removeStoredSessionId]);

  return {
    startHeartbeat,
    stopHeartbeat,
    emitRealtimeEvent,
    logout,
    getSessionId: () => sessionIdRef.current,
    clearSessionId: () => {
      sessionIdRef.current = null;
      removeStoredSessionId();
    },
    forceNewSessionId, // 🔒 NUEVO: Función para generar nuevo sessionId
    socket: socketRef.current,
    isActive: !!socketRef.current?.connected
  };
};
